import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart3, Plus, Search, ThumbsUp, Minus, ThumbsDown,
  Trash2, RefreshCw, Send, TrendingUp, Users, MessageSquare
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExportButton } from '../components/shared/ExportButton';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Acronym } from '../components/shared/AcronymTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const NPS_COLOR = (score) => score >= 50 ? 'text-emerald-600' : score >= 0 ? 'text-amber-600' : 'text-red-600';

const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}{suffix}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function NPSSurveyPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [responses, setResponses] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showCreate, setShowCreate] = useState(false);
  const [showAddResponse, setShowAddResponse] = useState(null);
  const [form, setForm] = useState({ name: '', question: 'How likely are you to recommend us to a friend or colleague?', follow_up_question: "What's the main reason for your score?", channel: 'email', target_audience: 'all_customers' });
  const [respForm, setRespForm] = useState({ respondent_name: '', respondent_email: '', score: 8, feedback: '' });

  const fetchData = useCallback(async () => {
    try {
      const [c, r, d] = await Promise.all([
        axios.get(`${API}/api/nps/campaigns`),
        axios.get(`${API}/api/nps/responses`),
        axios.get(`${API}/api/nps/dashboard`),
      ]);
      setCampaigns(c.data.campaigns || []);
      setResponses(r.data.responses || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load NPS data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Campaign name required'); return; }
    try {
      await axios.post(`${API}/api/nps/campaigns`, form);
      toast.success('Campaign created!');
      setShowCreate(false);
      setForm({ name: '', question: 'How likely are you to recommend us to a friend or colleague?', follow_up_question: "What's the main reason for your score?", channel: 'email', target_audience: 'all_customers' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleAddResponse = async () => {
    if (!showAddResponse) return;
    try {
      await axios.post(`${API}/api/nps/responses`, { ...respForm, campaign_id: showAddResponse.id });
      toast.success('Response recorded!');
      setShowAddResponse(null);
      setRespForm({ respondent_name: '', respondent_email: '', score: 8, feedback: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await axios.delete(`${API}/api/nps/campaigns/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const activateCampaign = async (id) => {
    try { await axios.put(`${API}/api/nps/campaigns/${id}`, { status: 'active' }); toast.success('Campaign activated'); fetchData(); } catch { toast.error('Failed'); }
  };

  const getCategory = (score) => score <= 6 ? 'detractor' : score <= 8 ? 'passive' : 'promoter';
  const getCategoryColor = (cat) => cat === 'promoter' ? 'bg-emerald-100 text-emerald-700' : cat === 'passive' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  const totalResp = dashboard.total_responses || 0;
  const pct = (n) => totalResp > 0 ? Math.round((n / totalResp) * 100) : 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="nps-survey-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary"><Acronym term="NPS" /> Surveys<PageInfoTooltip pageKey="nps-surveys" /></h1>
          <p className="text-gray-medium text-sm mt-1">Measure customer loyalty with Net Promoter Score</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={responses.map(r => ({ score: r.score, feedback: r.feedback, respondent: r.respondent_email, date: r.created_at }))}
            filename="nps_responses"
            columns={['score', 'feedback', 'respondent', 'date']}
          />
          <Button onClick={() => setShowCreate(true)} data-testid="create-nps-btn"><Plus className="h-4 w-4 mr-1" />New Campaign</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={BarChart3} label="NPS Score" value={dashboard.overall_nps || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={ThumbsUp} label="Promoters" value={`${dashboard.promoters || 0} (${pct(dashboard.promoters)}%)`} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={Minus} label="Passives" value={`${dashboard.passives || 0} (${pct(dashboard.passives)}%)`} color="bg-amber-50 text-amber-600" />
        <StatCard icon={ThumbsDown} label="Detractors" value={`${dashboard.detractors || 0} (${pct(dashboard.detractors)}%)`} color="bg-red-50 text-red-600" />
      </div>

      {/* NPS Gauge */}
      {totalResp > 0 && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className={cn("text-4xl font-bold", NPS_COLOR(dashboard.overall_nps))}>{dashboard.overall_nps}</p>
                <p className="text-xs text-gray-medium mt-1">Overall NPS</p>
              </div>
              <div className="flex-1">
                <div className="flex h-4 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 transition-all" style={{ width: `${pct(dashboard.promoters)}%` }} />
                  <div className="bg-amber-400 transition-all" style={{ width: `${pct(dashboard.passives)}%` }} />
                  <div className="bg-red-500 transition-all" style={{ width: `${pct(dashboard.detractors)}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-medium mt-1">
                  <span>Promoters {pct(dashboard.promoters)}%</span>
                  <span>Passives {pct(dashboard.passives)}%</span>
                  <span>Detractors {pct(dashboard.detractors)}%</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-text-primary">{dashboard.avg_score || 0}</p>
                <p className="text-xs text-gray-medium">Avg Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList><TabsTrigger value="campaigns">Campaigns ({campaigns.length})</TabsTrigger><TabsTrigger value="responses">Responses ({responses.length})</TabsTrigger></TabsList>

        <TabsContent value="campaigns" className="mt-4">
          <Card className="bg-white border-gray-border">
            <CardContent className="pt-4">
              {campaigns.length === 0 ? (
                <div className="text-center py-12 text-gray-medium"><BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No NPS campaigns yet</p></div>
              ) : (
                <div className="space-y-2">
                  {campaigns.map(c => (
                    <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 transition-colors" data-testid={`nps-campaign-${c.id}`}>
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><BarChart3 className="h-4 w-4 text-blue-600" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-text-primary">{c.name}</span>
                          <Badge className={cn("text-[10px]", c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600')}>{c.status}</Badge>
                        </div>
                        <p className="text-xs text-gray-medium">{c.response_count || 0} responses · NPS: {c.nps_score ?? '—'}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {c.status === 'draft' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => activateCampaign(c.id)}>Activate</Button>}
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setShowAddResponse(c); setRespForm({ respondent_name: '', respondent_email: '', score: 8, feedback: '' }); }}><Plus className="h-3 w-3 mr-0.5" />Response</Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteCampaign(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="responses" className="mt-4">
          <Card className="bg-white border-gray-border">
            <CardContent className="pt-4">
              {responses.length === 0 ? (
                <div className="text-center py-12 text-gray-medium"><MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No responses yet</p></div>
              ) : (
                <div className="space-y-2">
                  {responses.map(r => {
                    const cat = r.category || getCategory(r.score);
                    return (
                      <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border" data-testid={`nps-response-${r.id}`}>
                        <div className={cn("w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm", cat === 'promoter' ? 'bg-emerald-100 text-emerald-700' : cat === 'passive' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>{r.score}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-text-primary">{r.respondent_name || 'Anonymous'}</span>
                            <Badge className={cn("text-[10px] capitalize", getCategoryColor(cat))}>{cat}</Badge>
                          </div>
                          <p className="text-xs text-gray-medium truncate">{r.feedback || 'No feedback'}</p>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New NPS Campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Campaign Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="nps-campaign-name" /></div>
            <div><Label>NPS Question</Label><Textarea value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} rows={2} /></div>
            <div><Label>Follow-up Question</Label><Input value={form.follow_up_question} onChange={e => setForm(f => ({ ...f, follow_up_question: e.target.value }))} /></div>
            <div><Label>Channel</Label>
              <Select value={form.channel} onValueChange={v => setForm(f => ({ ...f, channel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="in_app">In-App</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-nps-campaign-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Response Dialog */}
      <Dialog open={!!showAddResponse} onOpenChange={() => setShowAddResponse(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Response — {showAddResponse?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Respondent Name</Label><Input value={respForm.respondent_name} onChange={e => setRespForm(f => ({ ...f, respondent_name: e.target.value }))} data-testid="nps-resp-name" /></div>
            <div><Label>Email</Label><Input value={respForm.respondent_email} onChange={e => setRespForm(f => ({ ...f, respondent_email: e.target.value }))} /></div>
            <div>
              <Label>Score (0-10)</Label>
              <div className="flex gap-1 mt-1">
                {[...Array(11)].map((_, i) => (
                  <button key={i} className={cn("w-8 h-8 rounded-lg text-xs font-medium transition-colors border", respForm.score === i ? (i <= 6 ? 'bg-red-500 text-white border-red-500' : i <= 8 ? 'bg-amber-500 text-white border-amber-500' : 'bg-emerald-500 text-white border-emerald-500') : 'bg-white border-gray-border text-gray-medium hover:bg-gray-50')} onClick={() => setRespForm(f => ({ ...f, score: i }))}>{i}</button>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-1">0-6 Detractor · 7-8 Passive · 9-10 Promoter</p>
            </div>
            <div><Label>Feedback</Label><Textarea value={respForm.feedback} onChange={e => setRespForm(f => ({ ...f, feedback: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddResponse(null)}>Cancel</Button><Button onClick={handleAddResponse} data-testid="save-nps-response-btn">Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
